import React, { useState } from 'react';
import { Table, Tag, Space, Button, Empty, Tooltip, message } from 'antd';
import { DownloadOutlined, InstagramFilled } from '@ant-design/icons';

export default function FlowInsights({
    data = [],
    loading = false,
    automationName = "Automation",
    onClose
}) {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [selectedTagFilter, setSelectedTagFilter] = useState([]);

    // Format date helper
    const formatDate = (istTimestamp) => {
        if (!istTimestamp) return '-';
        try {
            // Convert IST timestamp string to a Date object
            // Assuming format like "2025-10-17 08:10:01.315216"
            // If it's already a standard string, new Date() handles it
            let date = new Date(istTimestamp);

            // If invalid, try appending UTC if it assumes server time is UTC
            if (isNaN(date.getTime())) {
                date = new Date(istTimestamp + " UTC");
            }

            if (isNaN(date.getTime())) return istTimestamp;

            return date.toLocaleString();
        } catch (e) {
            return istTimestamp;
        }
    };

    // CSV Download function
    const downloadCSV = () => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            message.warning("No data to download");
            return;
        }

        // Get all unique questions from all responses
        const allQuestions = new Set();
        data.forEach(record => {
            record.question_response?.forEach(qr => {
                if (qr.question) {
                    allQuestions.add(qr.question.replace(/\n/g, ' ').trim());
                }
            });
        });

        const questionHeaders = Array.from(allQuestions);

        // Build CSV headers
        const headers = [
            'Username',
            'Instagram ID',
            'Flow Completed',
            'Message Count',
            'Conversation Started',
            'Last Updated',
            ...questionHeaders
        ];

        // Build CSV rows
        const rows = data.map(record => {
            const questionResponses = {};
            record.question_response?.forEach(qr => {
                const questionKey = qr.question?.replace(/\n/g, ' ').trim();
                if (questionKey) {
                    questionResponses[questionKey] = qr.user_response || '';
                }
            });

            return [
                record.sender_detail?.username || 'N/A',
                record.sender_id || 'N/A',
                record.flow_completed ? 'Yes' : 'No',
                record.message_count || '0',
                record.created_at || '',
                record.updated_at || '',
                ...questionHeaders.map(q => questionResponses[q] || '')
            ];
        });

        // Escape CSV values
        const escapeCSV = (val) => {
            const str = String(val || '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        // Build CSV content
        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...rows.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${automationName.replace(/\s+/g, '_')}_insights.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success("CSV downloaded successfully!");
    };

    // Columns Configuration
    const columns = [
        {
            title: 'Contact Name',
            key: 'username',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {record?.sender_detail?.profile_pic_url && (
                        <img
                            src={record.sender_detail.profile_pic_url}
                            alt=""
                            style={{ width: 24, height: 24, borderRadius: '50%' }}
                        />
                    )}
                    <span style={{ fontWeight: 500 }}>
                        {record?.sender_detail?.username || record.sender_id || 'Unknown'}
                    </span>
                    {record?.sender_detail?.username && (
                        <a
                            href={`https://instagram.com/${record.sender_detail.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#E1306C', display: 'flex' }}
                        >
                            <InstagramFilled />
                        </a>
                    )}
                </div>
            ),
        },
        {
            title: 'Responses',
            key: 'responses',
            render: (_, record) => {
                const hasResponses = record?.question_response?.length > 0;
                const isExpanded = expandedRowKeys.includes(record.sender_id); // Using sender_id as key

                if (!hasResponses) return <span style={{ color: '#ccc' }}>-</span>;

                return (
                    <Tag
                        color="blue"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setExpandedRowKeys(prev =>
                                isExpanded
                                    ? prev.filter(key => key !== record.sender_id)
                                    : [...prev, record.sender_id]
                            );
                        }}
                    >
                        {record.question_response.length} response(s) {isExpanded ? '▲' : '▼'}
                    </Tag>
                );
            },
        },
        {
            title: 'Started At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => <span style={{ fontSize: 13, color: '#666' }}>{formatDate(text)}</span>
        },
        {
            title: 'Tags',
            key: 'tags',
            render: (_, record) => (
                <Space size={[0, 4]} wrap>
                    {record?.tags?.map((tag, idx) => (
                        <Tag key={idx} color="purple">{tag.name || tag}</Tag>
                    ))}
                    {(!record?.tags || record.tags.length === 0) && <span style={{ color: '#ccc', fontSize: 12 }}>No tags</span>}
                </Space>
            ),
        }
    ];

    // Expanded Row Render (Question Responses)
    const expandedRowRender = (record) => {
        if (!record?.question_response || record.question_response.length === 0) return null;

        const responseColumns = [
            { title: 'Question', dataIndex: 'question', key: 'question', width: '50%' },
            {
                title: 'Answer',
                dataIndex: 'user_response',
                key: 'user_response',
                render: (text) => <span style={{ fontWeight: 500, color: '#1890ff' }}>{text}</span>
            }
        ];

        return (
            <div style={{ margin: '10px 0', padding: '12px', background: '#f9f9f9', borderRadius: 8 }}>
                <h4 style={{ fontSize: 12, textTransform: 'uppercase', color: '#888', marginBottom: 8 }}>Collected Data</h4>
                <Table
                    columns={responseColumns}
                    dataSource={record.question_response}
                    pagination={false}
                    size="small"
                    rowKey={(item, index) => index}
                    bordered
                />
            </div>
        );
    };

    return (
        <div className="flow-insights">
            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <span style={{ fontSize: 14, color: '#666' }}>
                        Total Contacts: <strong>{data.length}</strong>
                    </span>
                </div>
                <Button
                    type="default"
                    icon={<DownloadOutlined />}
                    onClick={downloadCSV}
                    disabled={data.length === 0}
                >
                    Export CSV
                </Button>
            </div>

            {/* Main Table */}
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="sender_id"
                pagination={{ pageSize: 10 }}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: (expanded, record) => {
                        setExpandedRowKeys(prev =>
                            expanded
                                ? [...prev, record.sender_id]
                                : prev.filter(key => key !== record.sender_id)
                        );
                    },
                    rowExpandable: record => record.question_response?.length > 0
                }}
                locale={{ emptyText: <Empty description="No contacts found yet" /> }}
            />
        </div>
    );
}
